import { useBoot } from '@app/hooks/useBoot';
import { ParameterizedRoutePath } from '@app/routes';
import { LazyRoutes, LoadReturn } from '@components/LazyRoutes';
import { useGetCarrierConnectCompanyAndUserTenantRelationshipsLazyQuery } from '@connect-carrier/generated/queries/getCarrierConnectCompanyAndUserTenantRelationships';
import { getSelectedCustomer } from '@connect-carrier/harness/customer';
import { useAuthHeader } from '@connect-carrier/hooks/useAuthHeader';
import { CustomerListOutput } from '@connect-carrier/hooks/useFetchCustomerList';
import {
  Roles,
  useHasAnyRole,
  useHasRole,
} from '@connect-carrier/hooks/useHasRole';
import { useFlagMe353268EnableCarrierPortalAvailableRoutes } from '@generated/flags/ME-353268-enable-carrier-portal-available-routes';
import { useFlagMe400205CarrierPortalTendersFrontend } from '@generated/flags/ME-400205-carrier-portal-tenders-frontend';
import { useFlagMe486626CarrierPortalAccountingQueueTabFe } from '@generated/flags/ME-486626-carrier-portal-accounting-queue-tab-fe';
import { useLazyRoutes } from '@hooks/useLazyRoutes';
import { FC, useEffect, useState } from 'react';

export const AppRoutes: FC = () => {
  const isCarrierPortalAvailableRoutesEnabled =
    useFlagMe353268EnableCarrierPortalAvailableRoutes();
  const isCarrierPortalTendersEnabled =
    useFlagMe400205CarrierPortalTendersFrontend();
  const isAccountingTabEnabled =
    useFlagMe486626CarrierPortalAccountingQueueTabFe();
  const selectedCustomer = getSelectedCustomer();

  if (selectedCustomer?.companyId && selectedCustomer?.tenantCode) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAuthHeader(selectedCustomer.companyId, selectedCustomer.tenantCode);
  }

  useBoot();

  const hasAdminAccess = useHasRole(Roles.Admin);
  const hasAccountingAccess = useHasRole(Roles.Accounting);
  const hasPricingAccess = useHasRole(Roles.Pricing);
  const hasOperationAccess = useHasRole(Roles.Operations);
  const exceptPricingRole = useHasAnyRole([
    Roles.Admin,
    Roles.Accounting,
    Roles.Operations,
    Roles.ReadOnly,
  ]);
  const exceptOpNAccRole = useHasAnyRole([
    Roles.Admin,
    Roles.Pricing,
    Roles.ReadOnly,
  ]);
  let onlyPricingRole = hasPricingAccess;
  if (hasPricingAccess) {
    if (exceptPricingRole) {
      onlyPricingRole = false;
    }
  }

  let showAvailableRoutes = false;
  let showOffers = false;
  if (hasOperationAccess || hasAccountingAccess) {
    if (exceptOpNAccRole) {
      showAvailableRoutes = true;
      showOffers = true;
    }
  } else {
    showAvailableRoutes = true;
    showOffers = true;
  }

  const [query] =
    useGetCarrierConnectCompanyAndUserTenantRelationshipsLazyQuery();
  async function fetchCustomerList(): Promise<
    ReadonlyArray<CustomerListOutput>
  > {
    return new Promise((resolve) => {
      query({
        variables: {
          startIndex: 0,
          pageSize: 100,
          endIndex: 100,
        },
        onCompleted: (data) => {
          const userTenantRelationships =
            data?.getCarrierConnectCompanyAndUserTenantRelationships
              .userTenantRelationships?.relationships;
          const companyRelationships =
            data?.getCarrierConnectCompanyAndUserTenantRelationships
              ?.companyRelationships;

          const result: CustomerListOutput[] = [];
          companyRelationships?.forEach((companyRelationship) => {
            const find = userTenantRelationships?.find(
              (userTenantRelationship) => {
                return (
                  userTenantRelationship.tenantId ===
                  companyRelationship.tenantId
                );
              },
            ) as CustomerListOutput;
            if (find) {
              result.push({
                ...find,
                companyId: `${companyRelationship.companyId}`,
                mmRecordId: `${companyRelationship.mmRecordId}`,
              });
            }
          });
          resolve(result);
        },
      });
    });
  }

  const [hasLoadedCustomerList, setHasLoadedCustomerList] = useState(false);
  const [hasAccessAdminRoutes, setHasAccessAdminRoutes] = useState(false);
  const [blockNonAdminRouteAccess, setBlockNonAdminRouteAccess] =
    useState(false);

  useEffect(() => {
    (async (): Promise<void> => {
      const customerList = await fetchCustomerList();
      setHasLoadedCustomerList(true);
      if (!customerList.length) {
        setBlockNonAdminRouteAccess(true);
        if (hasAdminAccess) {
          setHasAccessAdminRoutes(true);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAdminAccess]);

  const routes = useLazyRoutes(
    () => [
      {
        path: '/',
        exact: true,
        load: (): LoadReturn => import('../views/customer-landing/index'),
      },
      ...(!onlyPricingRole && !blockNonAdminRouteAccess
        ? [
            {
              path: '/my-routes' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn =>
                import('../views/my-routes/route-overview/index'),
            },
            {
              path: '/my-routes/:id' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn =>
                import('../views/my-routes/route-overview/index'),
            },
            {
              path: '/my-routes/:id/driver' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn => import('../views/my-routes/driver/index'),
            },
            {
              path: '/route-details/:id' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn => import('../common/RouteDetails/index'),
            },
            {
              path: '/route-details' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn => import('../common/RouteDetails/index'),
            },
            {
              path: '/add-driver-arrival' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn =>
                import('../views/my-routes/add-stop-event/index'),
            },
            {
              path: '/add-driver-departure' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn =>
                import('../views/my-routes/add-stop-event/index'),
            },
            {
              path: '/Documents/:id/' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn => import('../common/Documents/addDocument'),
            },
          ]
        : []),
      ...(isCarrierPortalAvailableRoutesEnabled &&
      showAvailableRoutes &&
      !blockNonAdminRouteAccess
        ? [
            {
              path: '/available-routes' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn => import('../views/available-routes/index'),
            },
            {
              path: '/available-route-details/:id' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn => import('../common/RouteDetails/index'),
            },
            {
              path: '/available-route-details' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn => import('../common/RouteDetails/index'),
            },
          ]
        : []),
      ...(showOffers && !blockNonAdminRouteAccess
        ? [
            {
              path: '/offers' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn => import('../views/offers/index'),
            },
            {
              path: '/offer-details/:id' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn => import('../common/RouteDetails/index'),
            },
            {
              path: '/offer-details' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn => import('../common/RouteDetails/index'),
            },
            {
              path: '/submit-offer' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn => import('../common/SubmitOffer/index'),
            },
          ]
        : []),
      ...(isCarrierPortalTendersEnabled
        ? [
            {
              path: '/tenders' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn => import('../views/tenders/index'),
            },
            {
              path: '/tender-details/:id' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn => import('../common/RouteDetails/index'),
            },
            {
              path: '/tender-details' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn => import('../common/RouteDetails/index'),
            },
          ]
        : []),
      ...(isAccountingTabEnabled && !blockNonAdminRouteAccess
        ? [
            {
              path: '/accounting' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn => import('../views/accounting/index'),
            },
            {
              path: '/accounting-route-details/:id' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn => import('../common/RouteDetails/index'),
            },
            {
              path: '/accounting-route-details/:id/driver' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn => import('../views/my-routes/driver/index'),
            },
            {
              path: '/accounting-route-details' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn => import('../common/RouteDetails/index'),
            },
          ]
        : []),
      ...(hasAdminAccess || hasAccessAdminRoutes
        ? [
            {
              path: '/company-account-settings' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn =>
                import('../views/company-account-settings/index'),
            },
          ]
        : []),
      ...(hasAdminAccess || hasAccessAdminRoutes
        ? [
            {
              path: '/user-management' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn => import('../views/user-management/index'),
            },
            {
              path: '/user-details' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn =>
                import('../views/user-management/user-details/index'),
            },
            {
              path: '/user-management/view-roles' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn =>
                import('../views/user-management/roles/index'),
            },
            {
              path: '/user-management/invite-new-user' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn =>
                import('../views/user-management/invite-new-user/index'),
            },
            {
              path: '/resend-invite' as ParameterizedRoutePath,
              exact: true,
              load: (): LoadReturn =>
                import('../views/user-management/resend-invite/index'),
            },
          ]
        : []),
      {
        path: '/__debug',
        exact: true,
        load: (): LoadReturn => import('../routes/debug'),
      },
      {
        path: '*' as ParameterizedRoutePath,
        load: (): LoadReturn => import('../views/not-found/index'),
      },
    ],
    [
      hasAccessAdminRoutes,
      blockNonAdminRouteAccess,
      hasAdminAccess,
      onlyPricingRole,
      showAvailableRoutes,
      showOffers,
      isCarrierPortalAvailableRoutesEnabled,
      isCarrierPortalTendersEnabled,
      isAccountingTabEnabled,
    ],
  );
  const emptyRoutes = useLazyRoutes(() => [], []);
  if (!hasLoadedCustomerList) {
    return <LazyRoutes routes={emptyRoutes} fallbackRedirect={null as anyOk} />;
  }
  return <LazyRoutes routes={routes} fallbackRedirect={null as anyOk} />;
};
